import React, {
  useState,
} from "react"

import {
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline"

import PulseLoader
  from "react-spinners/PulseLoader"

import {
  useGetOrgIntentionQuestionsQuery,
} from "@/features/orgIntentionBoards/orgIntentionQuestionsApiSlice"


const EtOrgIgniterIntentionQuestionViewer = ({
  intentionBoardId,
}) => {

  // First Created - 08 Sep 2026
  // Author - Atul Pandey
  // Purpose - Read only Intention Board question viewer
  //           for organization Igniters.

  /*
  |--------------------------------------------------------------------------
  | 01 - State
  |--------------------------------------------------------------------------
  */

  const [
    showQuestions,
    setShowQuestions,
  ] =
    useState(
      true
    )

  /*
  |--------------------------------------------------------------------------
  | 02 - Get Questions
  |--------------------------------------------------------------------------
  */

  const {
    data:
      orgIntentionQuestionsData,

    isLoading,

    isFetching,

    isError,

    error,
  } =
    useGetOrgIntentionQuestionsQuery(
      {
        intentionBoardId,
      },
      {
        skip:
          !intentionBoardId,
      }
    )

  /*
  |--------------------------------------------------------------------------
  | 03 - Normalize Questions
  |--------------------------------------------------------------------------
  */

  const orgIntentionQuestions =
    orgIntentionQuestionsData
      ?.ids
      ?.map(
        id =>
          orgIntentionQuestionsData
            ?.entities[id]
      )
      ?.sort(
        (
          a,
          b
        ) =>
          (
            a
              ?.displaySequence ||
            a
              ?.questionNumber ||
            0
          ) -
          (
            b
              ?.displaySequence ||
            b
              ?.questionNumber ||
            0
          )
      ) ||
    []

  /*
  |--------------------------------------------------------------------------
  | 04 - Format Question Type
  |--------------------------------------------------------------------------
  */

  const getQuestionTypeLabel =
    questionType => {

      switch (
        questionType
      ) {

        case "reflection":
          return "Reflection"

        case "short_text":
          return "Short Text"

        case "long_text":
          return "Long Text"

        case "single_select":
          return "Single Select"

        case "multi_select":
          return "Multi Select"

        case "checkbox_confirmation":
          return "Confirmation"

        default:
          return questionType ||
            "Not Set"

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 05 - Format Question Purpose
  |--------------------------------------------------------------------------
  */

  const getQuestionPurposeLabel =
    questionPurpose => {

      switch (
        questionPurpose
      ) {

        case "reflection":
          return "Reflection"

        case "participation_intent":
          return "Participation Intent"

        case "in_person_confirmation":
          return "In Person Confirmation"

        case "other":
          return "Other"

        default:
          return questionPurpose ||
            "Not Set"

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 06 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="m-1 p-1 bg-white rounded">

      {/*
      |--------------------------------------------------------------------------
      | Header
      |--------------------------------------------------------------------------
      */}

      <div className="flex justify-between items-center m-1 p-1">

        <div className="flex justify-start items-center">

          <div
            onClick={
              () =>
                setShowQuestions(
                  prev =>
                    !prev
                )
            }
            className="m-1 ml-2 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-gray-100"
          >

            {showQuestions ? (

              <MinusIcon className="h-5 w-5 text-orange-600" />

            ) : (

              <PlusIcon className="h-4 w-4 text-orange-600" />

            )}

          </div>

          <div>

            <p className="font-bold text-lg text-gray-800 mx-2">
              Intention Questions
            </p>

            <p className="text-[10px] text-gray-500 mt-1">
              Questions presented to nominated candidates on this Intention Board.
            </p>

          </div>

          

        </div>

        {isFetching &&
          !isLoading && (

          <PulseLoader
            color={"#ea580c"}
            size={4}
          />

        )}

      </div>

      {showQuestions && (

        <div>

          {/*
          |--------------------------------------------------------------------------
          | Loading
          |--------------------------------------------------------------------------
          */}

          {isLoading && (

            <div className="m-2 p-2">

              <PulseLoader
                color={"#ea580c"}
                size={6}
              />

            </div>

          )}

          {/*
          |--------------------------------------------------------------------------
          | Error
          |--------------------------------------------------------------------------
          */}

          {isError && (

            <div className="bg-orange-50 rounded m-1 p-3">

              <p className="text-xs text-orange-700">

                {
                  error
                    ?.data
                    ?.message ||
                  "Unable to load Intention Board questions."
                }

              </p>

            </div>

          )}

          {/*
          |--------------------------------------------------------------------------
          | Empty State
          |--------------------------------------------------------------------------
          */}

          {!isLoading &&
            !isError &&
            orgIntentionQuestions
              ?.length ===
              0 && (

            <div className="bg-gray-100 rounded m-1 p-1">

              <div className="bg-white rounded m-1 p-3">

                <p className="text-xs text-gray-500">
                  No Intention Board questions have been added yet.
                </p>

              </div>

            </div>

          )}

          {/*
          |--------------------------------------------------------------------------
          | Questions
          |--------------------------------------------------------------------------
          */}

          {orgIntentionQuestions
            ?.length >
            0 && (

            <div className="bg-gray-200 rounded m-1 p-1">

              {orgIntentionQuestions
                ?.map(
                  (
                    question,
                    index
                  ) => {

                    const sortedOptions =
                      [
                        ...(
                          question
                            ?.options ||
                          []
                        ),
                      ]
                        .sort(
                          (
                            a,
                            b
                          ) =>
                            (
                              a
                                ?.displaySequence ||
                              0
                            ) -
                            (
                              b
                                ?.displaySequence ||
                              0
                            )
                        )

                    const isTextQuestion =
                      (
                        question
                          ?.questionType ===
                          "reflection" ||
                        question
                          ?.questionType ===
                          "short_text" ||
                        question
                          ?.questionType ===
                          "long_text"
                      )

                    return (

                      <div
                        key={
                          question
                            ?._id ||
                          index
                        }
                        className="bg-white rounded m-1 p-3"
                      >

                        {/*
                        |--------------------------------------------------------------------------
                        | Question Header
                        |--------------------------------------------------------------------------
                        */}

                        <div className="flex justify-between items-start">

                          <div className="flex-1">

                            <div className="flex justify-start items-start">

                              <div className="bg-orange-50 text-orange-700 rounded-full min-w-7 h-7 flex justify-center items-center mr-2">

                                <p className="text-[10px] font-bold">

                                  {
                                    question
                                      ?.questionNumber ||
                                    index +
                                      1
                                  }

                                </p>

                              </div>

                              <div className="flex-1">

                                <div className="flex justify-start items-center flex-wrap">

                                  <p className="font-bold text-sm text-gray-800">

                                    {
                                      question
                                        ?.questionText
                                    }

                                  </p>

                                  {question
                                    ?.isRequired && (

                                    <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full ml-2 px-2 py-1">
                                      Required
                                    </p>

                                  )}

                                  {!question
                                    ?.isActive && (

                                    <p className="text-[9px] bg-gray-200 text-gray-500 rounded-full ml-1 px-2 py-1">
                                      Inactive
                                    </p>

                                  )}

                                </div>

                                {/*
                                |--------------------------------------------------------------------------
                                | Description
                                |--------------------------------------------------------------------------
                                */}

                                {question
                                  ?.questionDescription && (

                                  <p className="text-xs text-gray-600 mt-2 leading-5 whitespace-pre-wrap">

                                    {
                                      question
                                        .questionDescription
                                    }

                                  </p>

                                )}

                              </div>

                            </div>

                          </div>

                        </div>

                        {/*
                        |--------------------------------------------------------------------------
                        | Response Guidance
                        |--------------------------------------------------------------------------
                        */}

                        {question
                          ?.responseGuidance && (

                          <div className="bg-orange-50 rounded mt-3 p-3">

                            <p className="text-[9px] uppercase font-bold text-orange-700">
                              Response Guidance
                            </p>

                            <p className="text-xs text-gray-700 mt-1 whitespace-pre-wrap leading-5">

                              {
                                question
                                  .responseGuidance
                              }

                            </p>

                          </div>

                        )}

                        {/*
                        |--------------------------------------------------------------------------
                        | Options
                        |--------------------------------------------------------------------------
                        */}

                        {sortedOptions
                          ?.length >
                          0 && (

                          <div className="mt-3">

                            <p className="text-[9px] uppercase font-bold text-gray-500">
                              Response Options
                            </p>

                            <div className="mt-1">

                              {sortedOptions
                                ?.map(
                                  (
                                    option,
                                    optionIndex
                                  ) => (

                                    <div
                                      key={
                                        option
                                          ?._id ||
                                        optionIndex
                                      }
                                      className="flex justify-start items-center bg-gray-50 rounded mt-1 p-2"
                                    >

                                      <div className="h-4 w-4 rounded-full border border-gray-300 mr-2 flex-shrink-0" />

                                      <p className="text-xs text-gray-700">

                                        {
                                          option
                                            ?.optionLabel
                                        }

                                      </p>

                                    </div>

                                  )
                                )}

                            </div>

                          </div>

                        )}

                        {/*
                        |--------------------------------------------------------------------------
                        | Question Metadata
                        |--------------------------------------------------------------------------
                        */}

                        <div className="border-t border-gray-100 mt-3 pt-2">

                          <div className="flex justify-start items-center flex-wrap">

                            <p className="text-[9px] text-gray-500 mr-4">

                              Type:{" "}

                              <span className="font-semibold text-gray-600">

                                {
                                  getQuestionTypeLabel(
                                    question
                                      ?.questionType
                                  )
                                }

                              </span>

                            </p>

                            <p className="text-[9px] text-gray-500 mr-4">

                              Purpose:{" "}

                              <span className="font-semibold text-gray-600">

                                {
                                  getQuestionPurposeLabel(
                                    question
                                      ?.questionPurpose
                                  )
                                }

                              </span>

                            </p>

                            {isTextQuestion && (

                              <>

                                {Number(
                                  question
                                    ?.minimumResponseLength ||
                                  0
                                ) >
                                  0 && (

                                  <p className="text-[9px] text-gray-500 mr-4">

                                    Minimum:{" "}

                                    <span className="font-semibold text-gray-600">

                                      {
                                        question
                                          ?.minimumResponseLength
                                      }

                                    </span>

                                  </p>

                                )}

                                {question
                                  ?.maximumResponseLength !==
                                  null &&
                                  question
                                    ?.maximumResponseLength !==
                                  undefined &&
                                  question
                                    ?.maximumResponseLength !==
                                  "" && (

                                  <p className="text-[9px] text-gray-500 mr-4">

                                    Maximum:{" "}

                                    <span className="font-semibold text-gray-600">

                                      {
                                        question
                                          ?.maximumResponseLength
                                      }

                                    </span>

                                  </p>

                                )}

                              </>

                            )}

                          </div>

                        </div>

                      </div>

                    )

                  }
                )}

            </div>

          )}

        </div>

      )}

    </div>

  )

}

export default EtOrgIgniterIntentionQuestionViewer